// src/components/SimpleComponents/SimpleCodigoPostal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useMemo, useRef } from 'react';
import { Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { isValidCodigoPostal, returnOnlyNumbers } from 'utils/helpers';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from './SimpleFormProvider';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import CepInput from 'layouts/SharedComponents/CepInput';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CP_MASK = '9999-999'; // PT postal code (Codigo Postal)

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
// PT counterpart of SimpleCep. Same zip-state machinery (the provider's
// handleAddressZipBlur branches by country to GeoAPI.pt); only mask + validation
// differ (7 digits NNNN-NNN instead of 8-digit CEP).
const SimpleCodigoPostal = ({ name, subdocumentField, disabled, ...props }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const {
    locked,
    setFormZipState,
    fieldsWithError,
    disabledFields,
    handleAddressZipChange,
    data,
    form,
    formTmpState,
  } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName, flattenParentFieldName } = elementProps;

  const currentValue = Form.useWatch(fullFieldName, form);

  const dataValue = getValueFromFlattenObj(data, flattenFieldName);
  const zipState = formTmpState?.zip?.[flattenFieldName] || null;
  const runOnce = useRef(false);

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus(
    {
      ...props,
      ...elementProps,
      loading: !!zipState?.searching,
    },
    isValidCodigoPostal
  );

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  const { finalStatus, finalMessage } = useMemo(() => {
    const cleanCp = returnOnlyNumbers(currentValue);

    // Priority 1: Validating (searching postal code)
    if (zipState?.searching) {
      return { finalStatus: 'validating', finalMessage: '' };
    }

    // Priority 2: Empty field - check for required error from context
    if (!cleanCp) {
      const ctxMessageKey = fieldsWithError?.[flattenFieldName];
      if (ctxMessageKey) {
        return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
      }
      return { finalStatus: '', finalMessage: '' };
    }

    // Priority 3: Invalid postal code format
    if (!isValidCodigoPostal(cleanCp)) {
      return { finalStatus: 'error', finalMessage: baseMessage || translateX('invalid_zip') };
    }

    // Priority 4: Valid postal code
    return { finalStatus: 'success', finalMessage: '' };
  }, [currentValue, fieldsWithError, flattenFieldName, zipState, baseMessage]);

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (runOnce.current || !data) return;

    const savedCp = returnOnlyNumbers(dataValue);

    if (savedCp && !zipState?.data) {
      const addressData = flattenParentFieldName
        ? getValueFromFlattenObj(data, flattenParentFieldName)
        : data;

      if (addressData) {
        setFormZipState(flattenFieldName, {
          data: addressData,
          lastSearchFound: savedCp,
        });
        runOnce.current = true;
      }
    }
  }, [dataValue, flattenFieldName, flattenParentFieldName, setFormZipState, data, zipState]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleCodigoPostalChangeWrapper = ev => {
    handleChange(ev);

    handleAddressZipChange?.({
      ev,
      ...elementProps,
      value: ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '',
      subdocumentField,
    });
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={finalStatus}
      customValidationMessage={finalMessage}
      showFeedback={false}
    >
      <CepInput
        ref={elementRef}
        id={flattenFieldName}
        mask={CP_MASK}
        onChange={handleCodigoPostalChangeWrapper}
        disabled={isDisabled}
        status={finalStatus}
        loading={zipState?.searching || false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCodigoPostal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
